import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A beautiful landscape with mountains and a lake at sunset",
 *   "input_image": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=",
 *   "seed": 42,
 *   "aspect_ratio": "16:9",
 *   "output_format": "png",
 *   "prompt_upsampling": false,
 *   "safety_tolerance": 2
 * }
 * ```
 */
export interface BlackforestlabsFluxKontextMaxParams {
  /**
   * 文本提示词，描述要生成或编辑的图像内容（必填）
   */
  prompt: string;
  /**
   * 第1张输入图像，支持Base64编码或URL格式（可选）
   */
  input_image?: string;
  /**
   * 第2张输入图像，支持Base64编码或URL格式（可选）
   */
  input_image_2?: string;
  /**
   * 第3张输入图像，支持Base64编码或URL格式（可选）
   */
  input_image_3?: string;
  /**
   * 第4张输入图像，支持Base64编码或URL格式（可选）
   */
  input_image_4?: string;
  /**
   * 随机种子，用于可重现的生成结果（可选）
   */
  seed?: number;
  /**
   * 图像宽高比，格式如 "16:9"，范围为 "21:9" 到 "9:21"（可选）
   */
  aspect_ratio?: string;
  /**
   * 输出格式，默认png
   * @default "png"
   * @values jpeg | png
   */
  output_format?: 'jpeg' | 'png';
  /**
   * 是否启用提示词自动增强，默认false
   * @default false
   */
  prompt_upsampling?: boolean;
  /**
   * 安全容忍度，范围0-6，值越高容忍度越高，默认2
   * @default 2
   * @range 0 - 6
   */
  safety_tolerance?: number;
}

export interface BlackforestlabsFluxKontextMaxResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * blackforestlabs_flux_kontext_max
 *
 * Black Forest Labs Flux Kontext Max 图像生成接口，支持基于多张图像上下文的图像编辑和生成。
 * 
 * Model: flux-kontext-max
 * Features:
 * - 支持最多4张输入图像作为上下文
 * - 支持灵活的宽高比设置（21:9 到 9:21）
 * - 支持JPEG和PNG输出格式
 * - 支持提示词自动增强
 * - 支持安全内容控制（0-6级别）
 * - 异步任务处理，自动轮询获取结果
 *
 * @example
 * ```typescript
 * import { initSeacloud, blackforestlabsFluxKontextMax } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await blackforestlabsFluxKontextMax(
 *   {
 *     "prompt": "A beautiful landscape with mountains and a lake at sunset",
 *     "input_image": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=",
 *     "seed": 42,
 *     "aspect_ratio": "16:9",
 *     "output_format": "png",
 *     "prompt_upsampling": false,
 *     "safety_tolerance": 2
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 BlackforestlabsFluxKontextMaxParams 接口定义
 * @returns 生成的资源列表
 */
export async function blackforestlabsFluxKontextMax(
  params: BlackforestlabsFluxKontextMaxParams,
): Promise<BlackforestlabsFluxKontextMaxResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'blackforestlabs_flux_kontext_max',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: BlackforestlabsFluxKontextMaxResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
